import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, MapPin, CalendarDays } from "lucide-react";
import { API } from "../App";
import { useLang } from "../i18n/i18n";

const today = () => new Date().toISOString().split("T")[0];

export default function AppointmentForm({ details = "" }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", time: "", location: "Dans Montréal" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/appointments`, { ...form, details });
      setDone(true);
      toast.success(t("apt.successTitle"));
    } catch {
      toast.error(t("toast.sendFail"));
    } finally {
      setSending(false);
    }
  };

  const field =
    "mt-1.5 w-full rounded-lg border border-cream/15 bg-ink/70 px-4 py-3 text-cream outline-none focus:border-gold transition-colors";
  const label = "text-xs uppercase tracking-[0.2em] text-mutedc";
  const locBtn = (value) =>
    `flex items-center gap-2.5 rounded-xl border px-4 py-3.5 text-sm transition-all text-left ${
      form.location === value
        ? "border-gold bg-gold/10 text-goldlight"
        : "border-cream/15 text-cream/60 hover:border-gold/40 hover:text-cream"
    }`;

  if (done)
    return (
      <div data-testid="appointment-success" className="rounded-2xl border border-emerald-400/30 bg-emerald-400/5 p-8 sm:p-10 text-center">
        <CheckCircle2 size={42} className="mx-auto text-emerald-400" strokeWidth={1.5} />
        <h3 className="mt-4 font-display text-2xl text-cream">{t("apt.successTitle")}</h3>
        <p className="mt-3 text-sm leading-relaxed text-cream/70 max-w-sm mx-auto">
          {t("apt.successPre")}
          {form.name}
          {t("apt.successMid")}
          {form.phone}
          {t("apt.successEnd")}
        </p>
        <button
          onClick={() => {
            setDone(false);
            setForm({ name: "", email: "", phone: "", date: "", time: "", location: t("apt.in") });
          }}
          data-testid="appointment-again-btn"
          className="mt-6 rounded-full border border-cream/20 px-6 py-2.5 text-sm text-cream/80 hover:border-gold hover:text-goldlight transition-all"
        >
          {t("apt.another")}
        </button>
      </div>
    );

  return (
    <form onSubmit={submit} data-testid="appointment-form" className="space-y-4">
      {details && (
        <p className="rounded-lg border border-gold/25 bg-gold/5 px-4 py-3 text-sm text-goldlight tabular" data-testid="appointment-details-recap">
          {details}
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={label}>{t("apt.name")}</label>
          <input type="text" required value={form.name} onChange={set("name")} placeholder={t("apt.namePh")} data-testid="appointment-name-input" className={field} />
        </div>
        <div>
          <label className={label}>{t("apt.phone")}</label>
          <input type="tel" required value={form.phone} onChange={set("phone")} placeholder={t("apt.phonePh")} data-testid="appointment-phone-input" className={field} />
        </div>
      </div>
      <div>
        <label className={label}>{t("apt.email")}</label>
        <input type="email" required value={form.email} onChange={set("email")} placeholder={t("apt.emailPh")} data-testid="appointment-email-input" className={field} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={label}>{t("apt.date")}</label>
          <input type="date" required min={today()} value={form.date} onChange={set("date")} data-testid="appointment-date-input" className={field} />
        </div>
        <div>
          <label className={label}>{t("apt.time")}</label>
          <input type="time" required value={form.time} onChange={set("time")} data-testid="appointment-time-input" className={field} />
        </div>
      </div>
      <div>
        <label className={label}>
          <MapPin size={12} className="inline mr-1.5 -mt-0.5" /> {t("apt.youAre")}
        </label>
        <div className="mt-2 grid grid-cols-2 gap-3">
          <button type="button" onClick={() => setForm({ ...form, location: t("apt.in") })} data-testid="appointment-location-montreal" className={locBtn(t("apt.in"))}>
            <MapPin size={16} className="shrink-0" /> {t("apt.in")}
          </button>
          <button type="button" onClick={() => setForm({ ...form, location: t("apt.out") })} data-testid="appointment-location-exterieur" className={locBtn(t("apt.out"))}>
            <MapPin size={16} className="shrink-0" /> {t("apt.out")}
          </button>
        </div>
      </div>
      <p className="text-xs leading-relaxed text-mutedc flex items-start gap-2">
        <CalendarDays size={14} className="mt-0.5 shrink-0 text-gold" />
        {t("apt.note")}
      </p>
      <button
        type="submit"
        disabled={sending}
        data-testid="appointment-submit"
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-4 text-sm font-semibold text-ink transition-all hover:bg-goldlight disabled:opacity-60"
      >
        {sending ? <Loader2 size={16} className="animate-spin" /> : <CalendarDays size={16} />}
        {sending ? t("apt.sending") : t("apt.submit")}
      </button>
    </form>
  );
}
